import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Plus, Receipt, X, Check } from "lucide-react";
import { MobileShell } from "@/components/MobileShell";
import { useAuth } from "@/hooks/useAuth";
import {
  listMyBillSplits,
  listSplitParticipants,
  createBillSplit,
  markSharePaid,
  declineShare,
  cancelSplit,
  type BillSplit,
  type BillSplitParticipant,
} from "@/domains/billsplit";
import { formatMoney, parseAmountToCents } from "@/lib/money";
import { supabase } from "@/integrations/supabase/client";

export default function BillSplit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [splits, setSplits] = useState<BillSplit[]>([]);
  const [participantsBySplit, setParticipantsBySplit] = useState<Record<string, BillSplitParticipant[]>>({});
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [cashtags, setCashtags] = useState(""); // comma/space separated
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const list = await listMyBillSplits();
      setSplits(list);
      const entries = await Promise.all(
        list.map(async (s) => [s.id, await listSplitParticipants(s.id)] as const)
      );
      setParticipantsBySplit(Object.fromEntries(entries));
    } catch (e: any) {
      toast.error(e.message ?? "Failed to load");
    }
  }
  useEffect(() => { refresh(); }, []);

  async function resolveCashtags(raw: string): Promise<string[]> {
    const tags = raw.split(/[\s,]+/).map(t => t.replace(/^\$/, "").trim().toLowerCase()).filter(Boolean);
    if (tags.length === 0) return [];
    const { data, error } = await supabase
      .from("profiles")
      .select("id, cashtag")
      .in("cashtag", tags);
    if (error) throw error;
    const found = (data ?? []).map((r: any) => r.id as string);
    const missing = tags.filter(t => !(data ?? []).some((r: any) => r.cashtag === t));
    if (missing.length) throw new Error(`Unknown cashtags: ${missing.join(", ")}`);
    return found;
  }

  async function handleCreate() {
    const cents = parseAmountToCents(amount);
    if (!cents) return toast.error("Enter a valid amount");
    if (!title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const ids = await resolveCashtags(cashtags);
      const all = user?.id ? Array.from(new Set([user.id, ...ids])) : ids;
      if (all.length === 0) return toast.error("Add at least one participant");
      await createBillSplit({
        title: title.trim(),
        total_cents: cents,
        participants: all.map(id => ({ user_id: id })),
        notes: notes.trim() || undefined,
      });
      toast.success("Bill split created");
      setShowNew(false);
      setTitle(""); setAmount(""); setCashtags(""); setNotes("");
      refresh();
    } catch (e: any) {
      toast.error(e.message ?? "Failed");
    } finally { setBusy(false); }
  }

  async function payShare(split: BillSplit, share: BillSplitParticipant) {
    // Route through existing Send flow, pre-filled to creator
    const { data } = await supabase.from("profiles").select("cashtag").eq("id", split.creator_id).single();
    const tag = (data as any)?.cashtag;
    if (!tag) return toast.error("Creator cashtag missing");
    // Mark optimistically? Leave for user to confirm after sending.
    navigate(`/send?to=${tag}&amount=${(share.share_cents / 100).toFixed(2)}&memo=${encodeURIComponent("Bill: " + split.title)}&split=${split.id}`);
  }

  async function handleMarkPaid(splitId: string) {
    try { await markSharePaid(splitId); toast.success("Marked paid"); refresh(); }
    catch (e: any) { toast.error(e.message); }
  }

  async function handleDecline(p: BillSplitParticipant) {
    try { await declineShare(p.id); toast.success("Declined"); refresh(); }
    catch (e: any) { toast.error(e.message); }
  }

  async function handleCancel(s: BillSplit) {
    if (!confirm("Cancel this split?")) return;
    try { await cancelSplit(s.id); toast.success("Cancelled"); refresh(); }
    catch (e: any) { toast.error(e.message); }
  }

  return (
    <MobileShell>
      <div className="min-h-screen bg-background pb-24">
        <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
          <div className="px-4 py-3 flex items-center gap-3">
            <Link to="/wallet" className="p-2 -ml-2"><ArrowLeft className="w-5 h-5" /></Link>
            <div className="flex items-center gap-2 flex-1">
              <Receipt className="w-5 h-5 text-primary" />
              <h1 className="text-lg font-semibold">Bill Splitter</h1>
            </div>
            <Button size="sm" onClick={() => setShowNew(s => !s)}>
              <Plus className="w-4 h-4 mr-1" />New
            </Button>
          </div>
        </header>

        <div className="p-4 space-y-4">
          {showNew && (
            <Card className="p-4 space-y-3">
              <div className="text-sm font-medium">New split</div>
              <Input placeholder="What's it for? (e.g. Dinner)" value={title} onChange={(e) => setTitle(e.target.value)} />
              <Input placeholder="Total amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
              <Input placeholder="Cashtags: $alice $bob (you included automatically)" value={cashtags} onChange={(e) => setCashtags(e.target.value)} />
              <Textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
              <div className="flex gap-2">
                <Button onClick={handleCreate} disabled={busy} className="flex-1">
                  {busy ? "Creating…" : "Create split"}
                </Button>
                <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
              </div>
            </Card>
          )}

          {splits.length === 0 && !showNew && (
            <Card className="p-6 text-center text-sm text-muted-foreground">
              No bill splits yet. Tap <strong>New</strong> to start one.
            </Card>
          )}

          {splits.map((s) => {
            const ps = participantsBySplit[s.id] ?? [];
            const isCreator = s.creator_id === user?.id;
            const myShare = ps.find(p => p.user_id === user?.id);
            const paidCount = ps.filter(p => p.status === "paid").length;
            return (
              <Card key={s.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium">{s.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatMoney(s.total_cents)} · {paidCount}/{ps.length} paid
                    </div>
                  </div>
                  <Badge variant={s.status === "settled" ? "default" : s.status === "cancelled" ? "secondary" : "outline"}>
                    {s.status}
                  </Badge>
                </div>

                <ul className="space-y-1.5">
                  {ps.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-xs truncate">{p.user_id === user?.id ? "You" : p.user_id.slice(0, 8) + "…"}</span>
                        <span className="text-muted-foreground shrink-0">{formatMoney(p.share_cents)}</span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge variant={p.status === "paid" ? "default" : p.status === "declined" ? "secondary" : "outline"} className="text-xs">
                          {p.status}
                        </Badge>
                        {isCreator && s.status === "open" && p.status === "pending" && p.user_id !== user?.id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            onClick={async () => {
                              try { await markSharePaid(s.id, p.user_id); toast.success("Settled"); refresh(); }
                              catch (e: any) { toast.error(e.message); }
                            }}
                          >
                            <Check className="w-3 h-3 mr-1" />Settle
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                {s.status === "open" && myShare && myShare.status === "pending" && !isCreator && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Button size="sm" className="flex-1" onClick={() => payShare(s, myShare)}>
                      Pay {formatMoney(myShare.share_cents)}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleMarkPaid(s.id)}>
                      <Check className="w-4 h-4 mr-1" />Mark paid
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDecline(myShare)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {isCreator && s.status === "open" && (
                  <div className="flex justify-end pt-2 border-t">
                    <Button size="sm" variant="ghost" onClick={() => handleCancel(s)}>Cancel split</Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </MobileShell>
  );
}
